import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ENTITY_PATH = { customer: "/customers", supplier: "/suppliers", broker: "/brokers" };

export default function Ledger() {
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [entityFilter, setEntityFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (entityFilter) params.entity_type = entityFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = `${dateTo}T23:59:59.999999`;
    api.get("/ledger", { params }).then((r) => setTxns(r.data)).catch(() => {});
  }, [entityFilter, dateFrom, dateTo]);

  const totalReceivable = customers.reduce((s, c) => s + (c.balance || 0), 0);
  const totalPayable = suppliers.reduce((s, c) => s + (c.balance || 0), 0);

  return (
    <div className="space-y-5" data-testid="ledger-page">
      <div>
        <div className="label-mini">Receivables &amp; Payables</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Ledger</h1>
      </div>

      <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5">
          <div className="label-mini">Total Receivables</div>
          <div className="kpi-num text-3xl mt-2">{fmtMoney(totalReceivable)}</div>
          <div className="text-xs text-zinc-500 mt-1">Across {customers.length} customers</div>
        </div>
        <div className="bg-white p-5">
          <div className="label-mini">Total Payables</div>
          <div className="kpi-num text-3xl mt-2">{fmtMoney(totalPayable)}</div>
          <div className="text-xs text-zinc-500 mt-1">Across {suppliers.length} suppliers</div>
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="rounded-sm bg-zinc-100 border border-zinc-200">
          <TabsTrigger value="all" className="rounded-sm" data-testid="ledger-tab-all">All Transactions</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm" data-testid="ledger-tab-customers">Customer Ledger</TabsTrigger>
          <TabsTrigger value="suppliers" className="rounded-sm" data-testid="ledger-tab-suppliers">Supplier Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border border-zinc-200 rounded-sm text-xs px-2 py-1.5"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              data-testid="ledger-entity-filter"
            >
              <option value="">All entities</option>
              <option value="customer">Customers</option>
              <option value="supplier">Suppliers</option>
              <option value="broker">Brokers</option>
            </select>
            <input
              type="date"
              className="border border-zinc-200 rounded-sm text-xs px-2 py-1.5"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="ledger-date-from"
            />
            <span className="text-xs text-zinc-400">to</span>
            <input
              type="date"
              className="border border-zinc-200 rounded-sm text-xs px-2 py-1.5"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="ledger-date-to"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                className="text-xs underline text-zinc-500"
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                data-testid="ledger-date-clear"
              >
                Clear
              </button>
            )}
          </div>
          <div className="card-sharp overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="label-mini px-4 py-3">Date</th>
                  <th className="label-mini px-4 py-3">Entity</th>
                  <th className="label-mini px-4 py-3">Description</th>
                  <th className="label-mini px-4 py-3 text-right">Debit</th>
                  <th className="label-mini px-4 py-3 text-right">Credit</th>
                  <th className="label-mini px-4 py-3 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-zinc-100">
                    <td className="px-4 py-3 text-xs font-mono">{fmtDate(t.date)}</td>
                    <td className="px-4 py-3 text-xs">
                      <Link to={`${ENTITY_PATH[t.entity_type]}/${t.entity_id}`} className="font-semibold underline">
                        {t.entity_name}
                      </Link>
                      <span className="text-zinc-400"> · {t.entity_type}</span>
                    </td>
                    <td className="px-4 py-3 text-xs">{t.description}</td>
                    <td className="px-4 py-3 text-right font-mono tnum">{t.txn_type === "debit" ? fmtMoney(t.amount, "") : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono tnum">{t.txn_type === "credit" ? fmtMoney(t.amount, "") : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono tnum font-semibold">{fmtMoney(t.balance_after, "")}</td>
                  </tr>
                ))}
                {txns.length === 0 && (<tr><td colSpan={6} className="py-8 text-center text-xs text-zinc-500">No transactions.</td></tr>)}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-4">
          <div className="card-sharp overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="label-mini px-4 py-3">Code</th>
                  <th className="label-mini px-4 py-3">Name</th>
                  <th className="label-mini px-4 py-3">Type</th>
                  <th className="label-mini px-4 py-3 text-right">Balance (PKR)</th>
                  <th className="label-mini px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-t border-zinc-100">
                    <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-xs">{c.type}</td>
                    <td className="px-4 py-3 text-right font-mono tnum font-semibold">{fmtMoney(c.balance || 0, "")}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/customers/${c.id}`} className="text-xs font-semibold underline">View ledger</Link>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (<tr><td colSpan={5} className="py-8 text-center text-xs text-zinc-500">No customers.</td></tr>)}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="suppliers" className="mt-4">
          <div className="card-sharp overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="label-mini px-4 py-3">Code</th>
                  <th className="label-mini px-4 py-3">Name</th>
                  <th className="label-mini px-4 py-3">Country</th>
                  <th className="label-mini px-4 py-3 text-right">Balance (PKR)</th>
                  <th className="label-mini px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.id} className="border-t border-zinc-100">
                    <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-xs">{s.country}</td>
                    <td className="px-4 py-3 text-right font-mono tnum font-semibold">{fmtMoney(s.balance || 0, "")}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/suppliers/${s.id}`} className="text-xs font-semibold underline">View ledger</Link>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && (<tr><td colSpan={5} className="py-8 text-center text-xs text-zinc-500">No suppliers.</td></tr>)}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
